<script setup lang="ts">
import { useSidebarStore } from "@/stores/sidebar";

const route = useRoute();
const to = route.query.to as string;
const params = { to: to };

defineOptions({
  name: "AccountLayout",
});
onMounted(() => {
  useAuthGuardRedirection(params);
});

// Navigation for Account page
const { loadNavigationElements } = useNavigation();
const { logout } = useUser();
const router = useRouter();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const { data } = await useAsyncData("mainNavigation", () => {
  return loadNavigationElements({ depth: 2 });
});
provide("swNavigation-main-navigation", data);

const { loadNavigationElements: loadFooterNavigationElements } = useNavigation({
  type: "footer-navigation",
});
const { data: footerData } = await useAsyncData("mainFooterNavigation", () => {
  return loadFooterNavigationElements({ depth: 2 });
});

async function invokeLogout() {
  logout();
  router.push("/");
}

provide("swNavigation-footer-navigation", footerData);

// Add sidebar functionality
const sidebarStore = useSidebarStore();

const closeSidebar = () => {
  sidebarStore.closeSidebar();
};
</script>

<template>
  <div class="nuxt-inner">
    <LayoutHeader />
    <LayoutNotifications />

    <main class="flex-grow non-ssr-only">
      <section class="account-overview-wrapper py-12 md:py-16">
        <div class="container small">
          <div class="flex">
            <aside 
              class="sidebar-account overflow-y-auto bg-white fixed inset-0 lg:sticky lg:top-[107px] z-[1000] lg:z-10 w-[85%] md:w-3/6 lg:w-[380px] 3xl:w-[480px] border-0 lg:border-r lg:border-grey-100 lg:pr-7 xl:pr-10 -translate-x-full lg:translate-x-0 transition-all duration-300 ease-in-out"
            >
              <!-- Filter header -->
              <div class="px-5 lg:px-0 py-8 lg:py-0 sticky top-0 bg-white z-10 border-0 border-b lg:border-0 border-grey-100 h-[50px] flex items-center">
                <div class="flex justify-between items-center w-full">
                  <div class="title">
                    <h2 class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none">
                      {{ $t("account.menu.myAccount") }}
                    </h2>
                  </div>
                  <button 
                    class="text-xl block lg:hidden" 
                    aria-label="Close menu" 
                    id="closeSidebar"
                    @click="closeSidebar"
                  >
                    <i class="icon-close-outline" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <!-- Filter content -->
              <div class="content relative mt-6 lg:mt-10 pb-[120px] lg:pb-0">
                <!-- Filters content inner -->
                <div class="px-5 lg:px-0">
                  <!-- Filters panel -->
                  <div class="accordion sidebar-accordion order-2 xl:order-1">
                    <!-- 1 -->
                    <div class="border-b border-gray-200 group">
                      
                      <button
                        class="py-4 flex bg-transparent border-0 w-full items-center text-left cursor-pointer group-[.active-menu-item]:text-primary text-gray-700 hover:text-gray-900">
                        <i class="icon-overview text-xl mr-3" aria-hidden="true"></i>
                        <NuxtLink
                          :to="formatLink(`/account`)"
                          class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] group-[.active-menu-item]:text-primary">
                            {{ $t("account.menu.accountOverviewHeader") }}
                        </NuxtLink>
                      </button>
                    </div>

                    <!-- 2 -->
                    <div class="border-b border-gray-200 group">
                      <button
                        class="py-4 flex bg-transparent border-0 w-full items-center text-left cursor-pointer group-[.active-menu-item]:text-primary text-gray-700 hover:text-gray-900">
                        <i class="icon-verified-profile text-xl mr-3" aria-hidden="true"></i>
                        <NuxtLink
                              :to="formatLink(`/account/profile`)"
                              class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] group-[.active-menu-item]:text-primary"
                            >
                            {{ $t("account.yourProfile") }}
                        </NuxtLink>
                      </button>
                    </div>

                    <!-- 3 -->
                    <div class="border-b border-gray-200 group">
                      <button
                        class="py-4 flex bg-transparent border-0 w-full items-center text-left cursor-pointer group-[.active-menu-item]:text-primary text-gray-700 hover:text-gray-900">
                        <i class="icon-warehouse-alt text-xl mr-3" aria-hidden="true"></i>
                        <NuxtLink
                              :to="formatLink(`/account/address`)"
                              class="ffont-mulish font-semibold text-base 3xl:text-md max-w-[90%] group-[.active-menu-item]:text-primary"
                            >
                              {{ $t("account.yourAddress") }}
                        </NuxtLink>
                      </button>
                    </div>

                    <!-- 4 -->
                    <div class="border-b border-gray-200 group">
                      <button
                        class="py-4 flex bg-transparent border-0 w-full items-center text-left cursor-pointer group-[.active-menu-item]:text-primary text-gray-700 hover:text-gray-900">
                        <i class="icon-orders text-xl mr-3" aria-hidden="true"></i>
                        <NuxtLink
                              :to="formatLink(`/account/order`)"
                              class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] group-[.active-menu-item]:text-primary"
                            >
                            {{ $t("account.orderHistoryHeader") }}
                        </NuxtLink>
                      </button>
                    </div>

                    <!-- 5 -->
                    <div class="border-b border-gray-200 group">
                      <button
                        class="py-4 flex bg-transparent border-0 w-full items-center text-left cursor-pointer group-[.active-menu-item]:text-primary text-gray-700 hover:text-gray-900"
                        @click="invokeLogout()">
                        <i class="icon-logout text-xl mr-3" aria-hidden="true"></i>
                        <span
                          class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] group-[.active-menu-item]:text-primary"
                          > {{ $t("account.logout") }}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
            <div class="account-overview w-full lg:pl-7 xl:pl-10">
              <slot />
            </div>
          </div>

          <div
            v-if="sidebarStore.isSidebarOpen"
            class="overlay-close fixed inset-0 z-[10] bg-black opacity-50 lg:hidden" @click="closeSidebar"
          ></div>
        </div>
      </section>
    </main>

    <LayoutFooter />
    <LayoutFooterOverlay />
  </div>
</template>
